import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useNumberSeries, useSaveNumberingResetDate, useSaveNumberSeries } from '@/api/hooks'
import type { NumberSeriesView } from '@/api/types'
import { ErrorBanner, humanise, LoadingBlock } from '@/components/common'

const RESET_MODES = [
  { value: 'YEARLY', label: 'Yearly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'NEVER', label: 'Never' },
]

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** Days in a month, ignoring leap years: a reset date is a rule, not a date. */
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

/**
 * Document numbering: what each series is called, how wide it runs, and when it
 * starts again. Prefix edits take effect on the next number issued and leave the
 * running count alone, so a financial year never contains two number ones.
 */
export function NumberingTab() {
  const { data, isLoading, error } = useNumberSeries()
  const saveSeries = useSaveNumberSeries()
  const saveResetDate = useSaveNumberingResetDate()

  const [month, setMonth] = useState(4)
  const [day, setDay] = useState(1)

  useEffect(() => {
    if (data) {
      setMonth(data.resetMonth)
      setDay(data.resetDay)
    }
  }, [data])

  if (isLoading) return <LoadingBlock />
  if (error) return <ErrorBanner error={error} />
  if (!data) return null

  const resetChanged = month !== data.resetMonth || day !== data.resetDay
  const maxDay = DAYS_IN_MONTH[month - 1]

  return (
    <Stack spacing={3}>
      <ErrorBanner error={saveSeries.error} />
      <ErrorBanner error={saveResetDate.error} />

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Year end
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The day every yearly series starts again. Held once for the whole company so the finance
            documents cannot drift apart from one another.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <TextField
              select
              label="Reset month"
              value={month}
              onChange={(event) => {
                const next = Number(event.target.value)
                setMonth(next)
                setDay((current) => Math.min(current, DAYS_IN_MONTH[next - 1]))
              }}
              sx={{ minWidth: 180 }}
            >
              {MONTHS.map((name, index) => (
                <MenuItem key={name} value={index + 1}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Reset day"
              value={day}
              onChange={(event) => setDay(Number(event.target.value))}
              sx={{ minWidth: 120 }}
            >
              {Array.from({ length: maxDay }, (_, index) => index + 1).map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              disabled={!resetChanged || saveResetDate.isPending}
              onClick={() => saveResetDate.mutate({ month, day })}
              sx={{ mt: 0.5 }}
            >
              Save year end
            </Button>
          </Stack>
          <Alert severity="info" sx={{ mt: 2 }}>
            {month === 1 && day === 1
              ? 'A year starting 1 January is one calendar year, so numbers read INV/2026/00001.'
              : `A year starting ${day} ${MONTHS[month - 1]} spans two, so numbers read INV/2627/00001.`}
          </Alert>
        </CardContent>
      </Card>

      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Document series
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Changing a prefix affects the next number only. The count carries on from where it stands,
          and every number already issued keeps the form it was issued in.
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Document</TableCell>
              <TableCell>Prefix</TableCell>
              <TableCell>Digits</TableCell>
              <TableCell>Restarts</TableCell>
              <TableCell>Next no.</TableCell>
              <TableCell align="right">&nbsp;</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.series.map((series) => (
              <SeriesRow
                key={series.sequenceType}
                series={series}
                saving={saveSeries.isPending}
                onSave={(body) => saveSeries.mutate(body)}
              />
            ))}
          </TableBody>
        </Table>
      </Box>
    </Stack>
  )
}

function SeriesRow({
  series,
  saving,
  onSave,
}: {
  series: NumberSeriesView
  saving: boolean
  onSave: (body: {
    sequenceType: string
    prefix: string
    padding: number
    resetMode: string
    nextNumber: number
  }) => void
}) {
  const [prefix, setPrefix] = useState(series.prefix)
  const [padding, setPadding] = useState(series.padding)
  const [resetMode, setResetMode] = useState(series.resetMode)
  const [next, setNext] = useState(String(series.nextValue))

  // The server is the source of truth: fold in what it returned after a save.
  useEffect(() => {
    setPrefix(series.prefix)
    setPadding(series.padding)
    setResetMode(series.resetMode)
    setNext(String(series.nextValue))
  }, [series.prefix, series.padding, series.resetMode, series.nextValue])

  const dirty =
    prefix !== series.prefix ||
    padding !== series.padding ||
    resetMode !== series.resetMode ||
    next !== String(series.nextValue)

  return (
    <TableRow hover>
      <TableCell>{humanise(series.sequenceType)}</TableCell>
      <TableCell>
        <TextField
          value={prefix}
          onChange={(event) => setPrefix(event.target.value.toUpperCase().slice(0, 20))}
          sx={{ width: 110 }}
          inputProps={{ 'aria-label': `${series.sequenceType} prefix` }}
        />
      </TableCell>
      <TableCell>
        <TextField
          select
          value={padding}
          onChange={(event) => setPadding(Number(event.target.value))}
          sx={{ width: 80 }}
        >
          {[3, 4, 5, 6, 7, 8].map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </TextField>
      </TableCell>
      <TableCell>
        <TextField
          select
          value={resetMode}
          onChange={(event) => setResetMode(event.target.value)}
          sx={{ width: 130 }}
        >
          {RESET_MODES.map((mode) => (
            <MenuItem key={mode.value} value={mode.value}>
              {mode.label}
            </MenuItem>
          ))}
        </TextField>
      </TableCell>
      <TableCell>
        <TextField
          value={next}
          onChange={(event) => setNext(event.target.value.replace(/\D/g, '').slice(0, 9))}
          sx={{ width: 110 }}
          inputProps={{ 'aria-label': `${series.sequenceType} next number`, inputMode: 'numeric' }}
          helperText={series.nextNumber}
        />
      </TableCell>
      <TableCell align="right">
        <Button
          size="small"
          disabled={!dirty || !prefix.trim() || !next || Number(next) < 1 || saving}
          onClick={() =>
            onSave({
              sequenceType: series.sequenceType,
              prefix: prefix.trim(),
              padding,
              resetMode,
              nextNumber: Number(next),
            })
          }
        >
          Save
        </Button>
      </TableCell>
    </TableRow>
  )
}
